//Libs
import { useState, useEffect, useCallback } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import TrashIcon from "@heroicons/react/24/solid/TrashIcon";
import { v4 as uuidv4 } from "uuid";
import { Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import CheckIcon from "@heroicons/react/20/solid/CheckIcon";

//Local
import BaseModal from "./BaseModal";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";
import BaseInputText from "./BaseInputText";
import BaseInputNumber from "./BaseInputNumber";
import BaseInputTextArea from "./BaseInputTextArea";
import { ExistingPurchaseOrder, POLineItem } from "../models/purchase-order";
import { ExistingPriceBookItem } from "../models/price-book-item";
import { DbWrite } from "../database";
import { useAuthStore } from "../store/firebase-auth";
import { useToastMessageStore } from "../store/toast-messages";
import { createToastMessageID } from "../utils";
import currencyFormatter from "../currency";
import { useTypesenseStore } from "../store/typesense";
import { typesensePriceBookItemsQuery } from "../utils/typesenseQueries";
import { useSiteKeyDocStore } from "../store/site-key-doc";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: ExistingPurchaseOrder;
  siteKey: string;
}

interface FormData {
  lineItems: POLineItem[];
  totalTaxAmount: number;
}

// Enhanced Line Item Component with search
interface LineItemProps {
  index: number;
  field: Record<string, any>;
  control: any;
  errors: any;
  currency: string;
  onRemove: () => void;
  watch: any;
  setValue: any;
  siteKey: string;
  locationID: string | null;
}

function EditableLineItem({
  index,
  field,
  control,
  errors,
  currency,
  onRemove,
  watch,
  setValue,
  siteKey,
  locationID,
}: LineItemProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [priceBookItems, setPriceBookItems] = useState<ExistingPriceBookItem[]>(
    [],
  );
  const [selectedPBItem, setSelectedPBItem] =
    useState<ExistingPriceBookItem | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomItem, setIsCustomItem] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const typesenseSearchKey = useTypesenseStore(
    (state) => state.scopedSearchKey,
  );
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);

  // Check if this line item already has data (editing existing item)
  const currentTitle = watch(`lineItems.${index}.title`);
  const currentPriceBookItemID = watch(`lineItems.${index}.priceBookItemID`);

  useEffect(() => {
    // If the item already has data, expand it
    if (currentTitle || currentPriceBookItemID) {
      setIsExpanded(true);
      if (!currentPriceBookItemID) {
        setIsCustomItem(true);
      }
    }
  }, [currentTitle, currentPriceBookItemID]);

  // Search pricebook items using Typesense
  const searchPriceBookItems = useCallback(
    async (searchTerm: string) => {
      if (!typesenseSearchKey || !searchTerm.trim()) {
        setPriceBookItems([]);
        return;
      }

      setIsSearching(true);
      try {
        const pbItems = await typesensePriceBookItemsQuery(
          typesenseSearchKey,
          searchTerm,
        );

        // Filter by location if site has that setting enabled
        if (
          siteKeyDoc?.customizations.filterPriceBookItemsByLocationID ===
            true &&
          locationID
        ) {
          const filtered = pbItems.filter(
            (item) =>
              item.locationID === locationID || item.locationID === null,
          );
          setPriceBookItems(filtered);
        } else {
          setPriceBookItems(pbItems);
        }
      } catch (error) {
        console.error("Error searching pricebook items:", error);
        setPriceBookItems([]);
      } finally {
        setIsSearching(false);
      }
    },
    [typesenseSearchKey, locationID, siteKeyDoc],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchPriceBookItems(value);
  };

  const handleSelectPriceBookItem = (pbItem: ExistingPriceBookItem) => {
    setIsExpanded(true);
    setIsCustomItem(false);
    setSelectedPBItem(pbItem);
    setPriceBookItems([]);

    // Use cost if available, otherwise fall back to unitPrice
    const unitPrice =
      pbItem.cost !== null && pbItem.cost !== undefined
        ? pbItem.cost
        : pbItem.unitPrice;

    // Populate form fields with pricebook item data
    setValue(`lineItems.${index}.title`, pbItem.title);
    setValue(`lineItems.${index}.description`, pbItem.description || "");
    setValue(`lineItems.${index}.unitPrice`, unitPrice);
    setValue(`lineItems.${index}.priceBookItemID`, pbItem.id);
    setValue(`lineItems.${index}.quantity`, 1);
    setValue(`lineItems.${index}.subTotal`, unitPrice);
    setSearchQuery("");
  };

  const handleConfirmCustomItem = () => {
    if (!searchQuery.trim()) return;

    setIsExpanded(true);
    setIsCustomItem(true);
    setPriceBookItems([]);

    // Set the title and clear pricebook item ID
    setValue(`lineItems.${index}.title`, searchQuery.trim());
    setValue(`lineItems.${index}.priceBookItemID`, null);
    setValue(`lineItems.${index}.quantity`, 1);
    setValue(`lineItems.${index}.unitPrice`, 0);
    setValue(`lineItems.${index}.subTotal`, 0);
    setSearchQuery("");
  };

  // If not expanded, show search interface
  if (!isExpanded) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Combobox
              value={selectedPBItem}
              onChange={handleSelectPriceBookItem}
            >
              <div className="relative">
                <Combobox.Input
                  className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  displayValue={() => searchQuery}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      searchQuery.trim() &&
                      priceBookItems.length === 0
                    ) {
                      event.preventDefault();
                      handleConfirmCustomItem();
                    }
                  }}
                  placeholder="Search for items or enter custom item name..."
                />

                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setSearchQuery("")}
                >
                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {isSearching && (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        Searching...
                      </div>
                    )}

                    {priceBookItems.map((pbItem) => (
                      <Combobox.Option
                        key={pbItem.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active ? "bg-blue-600 text-white" : "text-gray-900"
                          }`
                        }
                        value={pbItem}
                      >
                        {({ selected, active }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span
                                  className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                                >
                                  {pbItem.title}
                                </span>
                                {pbItem.description && (
                                  <span
                                    className={`text-sm ${active ? "text-blue-200" : "text-gray-500"}`}
                                  >
                                    {pbItem.description}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm">
                                {currencyFormatter(pbItem.unitPrice, currency)}/
                                {pbItem.units}
                              </div>
                            </div>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? "text-white" : "text-blue-600"
                                }`}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))}

                    {searchQuery.trim() &&
                      priceBookItems.length === 0 &&
                      !isSearching && (
                        <div
                          className="relative cursor-default select-none px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={handleConfirmCustomItem}
                        >
                          Add "{searchQuery}" as custom item
                        </div>
                      )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="ml-2 p-2 text-red-500 hover:text-red-700"
            aria-label="Remove item"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Expanded view with form fields
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {isCustomItem ? "(Custom Item)" : "(From Price Book)"}
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-500 hover:text-red-700"
          aria-label="Remove item"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Line 1: Item name, quantity, unit price, subtotal */}
      <div className="mt-3 grid grid-cols-[2fr,1fr,1fr,1fr] gap-3">
        {/* Item Name */}
        <div>
          <Controller
            name={`lineItems.${index}.title`}
            control={control}
            rules={{ required: "Item name is required" }}
            render={({ field }) => (
              <BaseInputText
                text="Item Name"
                inputName={`lineItems.${index}.title`}
                admin={true}
                required={true}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={!isCustomItem} // Disable if from pricebook
              />
            )}
          />
          {errors.lineItems?.[index]?.title && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.title?.message}
            </div>
          )}
        </div>

        {/* Quantity */}
        <div>
          <Controller
            name={`lineItems.${index}.quantity`}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <BaseInputNumber
                text="Qty"
                inputName={`lineItems.${index}.quantity`}
                admin={true}
                required={true}
                value={rest.value === 0 ? "" : rest.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  if (value === "" || value === "0") {
                    rest.onChange(0);
                  } else {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      rest.onChange(numValue);
                    }
                  }
                  // Update subtotal immediately
                  const unitPrice = watch(`lineItems.${index}.unitPrice`) || 0;
                  const quantity = value === "" ? 0 : parseFloat(value) || 0;
                  setValue(`lineItems.${index}.subTotal`, quantity * unitPrice);
                }}
              />
            )}
          />
          {errors.lineItems?.[index]?.quantity && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.quantity?.message}
            </div>
          )}
        </div>

        {/* Unit Price */}
        <div>
          <Controller
            name={`lineItems.${index}.unitPrice`}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <BaseInputNumber
                text="Unit Price"
                inputName={`lineItems.${index}.unitPrice`}
                admin={true}
                required={true}
                value={rest.value === 0 ? "" : rest.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  if (value === "" || value === "0") {
                    rest.onChange(0);
                  } else {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      rest.onChange(numValue);
                    }
                  }
                  // Update subtotal immediately
                  const quantity = watch(`lineItems.${index}.quantity`) || 0;
                  const unitPrice = value === "" ? 0 : parseFloat(value) || 0;
                  setValue(`lineItems.${index}.subTotal`, quantity * unitPrice);
                }}
              />
            )}
          />
          {errors.lineItems?.[index]?.unitPrice && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.unitPrice?.message}
            </div>
          )}
        </div>

        {/* Subtotal (read-only) */}
        <div>
          <Controller
            name={`lineItems.${index}.subTotal`}
            control={control}
            render={({ field }) => (
              <BaseInputText
                text="Subtotal"
                inputName={`lineItems.${index}.subTotal`}
                admin={true}
                required={false}
                value={currencyFormatter(field.value || 0, currency)}
                onChange={() => {}} // Read-only
                disabled={true}
              />
            )}
          />
        </div>
      </div>

      {/* Line 2: Description and vendor part number */}
      <div className="mt-3 grid grid-cols-[2fr,1fr] gap-3">
        {/* Description */}
        <div>
          <Controller
            name={`lineItems.${index}.description`}
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                text="Description"
                inputName={`lineItems.${index}.description`}
                admin={true}
                required={false}
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={2}
              />
            )}
          />
        </div>

        {/* Vendor Part Number */}
        <div>
          <Controller
            name={`lineItems.${index}.vendorPartNumber`}
            control={control}
            render={({ field }) => (
              <BaseInputText
                text="Vendor Part Number"
                inputName={`lineItems.${index}.vendorPartNumber`}
                admin={true}
                required={false}
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default function EditItemsDialog(props: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      lineItems: props.purchaseOrder.lineItems || [],
      totalTaxAmount: props.purchaseOrder.totalTaxAmount || 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "lineItems",
  });

  const watchedLineItems = watch("lineItems");
  const watchedTaxAmount = watch("totalTaxAmount");

  // Calculate totals
  const subTotal =
    watchedLineItems?.reduce(
      (total, item) => total + (item.subTotal || 0),
      0,
    ) || 0;
  const totalAmount = subTotal + (watchedTaxAmount || 0);

  const addLineItem = () => {
    append({
      id: uuidv4(),
      title: "",
      description: null,
      quantity: 1,
      unitPrice: 0,
      subTotal: 0,
      priceBookItemID: null,
      vendorPartNumber: null,
      quantityReceived: 0,
    });
  };

  const onSubmit = async (data: FormData) => {
    if (!firebaseUser) return;

    setIsSubmitting(true);
    try {
      // Calculate new totals
      const newSubTotal = data.lineItems.reduce(
        (total, item) => total + (item.subTotal || 0),
        0,
      );
      const newTotalAmount = newSubTotal + (data.totalTaxAmount || 0);

      await DbWrite.purchaseOrders.update({
        id: props.purchaseOrder.id,
        refPath: props.purchaseOrder.refPath,
        lineItems: data.lineItems,
        subTotal: newSubTotal,
        totalAmount: newTotalAmount,
        totalTaxAmount: data.totalTaxAmount,
        lastModifiedBy: firebaseUser.uid,
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Items updated successfully",
        dialog: false,
        type: "success",
      });

      props.onClose();
    } catch (error) {
      console.error("Error updating items:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error updating items",
        dialog: false,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal
      open={props.isOpen}
      closeModal={props.onClose}
      title={
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <h1 className="inline-flex items-center text-xl font-semibold">
            Edit Items
          </h1>
          <button type="button" onClick={props.onClose}>
            <XMarkIcon aria-label="close dialog" className="h-6 text-white" />
          </button>
        </div>
      }
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-xl rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-h-[80vh] space-y-6 overflow-y-auto p-8"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Line Items</h3>
            <BaseButtonSecondary
              type="button"
              onClick={addLineItem}
              className="px-4 py-2"
            >
              Add Item
            </BaseButtonSecondary>
          </div>

          {fields.length === 0 && (
            <div className="rounded bg-gray-50 p-4 text-center text-gray-500">
              No items added. Click "Add Item" to add parts to this purchase
              order.
            </div>
          )}

          {fields.map((field, index) => (
            <EditableLineItem
              key={field.id}
              index={index}
              field={field}
              control={control}
              errors={errors}
              currency="USD"
              onRemove={() => remove(index)}
              watch={watch}
              setValue={setValue}
              siteKey={props.siteKey}
              locationID={null}
            />
          ))}
        </div>

        {/* Tax and Totals */}
        <div className="border-t bg-white pt-6">
          <div className="ml-auto max-w-xs space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">
                {currencyFormatter(subTotal, "USD")}
              </span>
            </div>

            <div>
              <Controller
                name="totalTaxAmount"
                control={control}
                rules={{
                  min: { value: 0, message: "Tax amount cannot be negative" },
                }}
                render={({ field }) => (
                  <BaseInputNumber
                    text="Tax Amount"
                    inputName="totalTaxAmount"
                    admin={true}
                    required={false}
                    value={field.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const numberValue = event.target.valueAsNumber;
                      field.onChange(isNaN(numberValue) ? 0 : numberValue);
                    }}
                  />
                )}
              />
              {errors.totalTaxAmount && (
                <div className="mt-1 text-sm text-red-500">
                  {errors.totalTaxAmount.message}
                </div>
              )}
            </div>

            <div className="flex justify-between border-t border-gray-200 pt-2">
              <span className="text-lg font-semibold text-gray-700">
                Total:
              </span>
              <span className="text-lg font-bold">
                {currencyFormatter(totalAmount, "USD")}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 border-t pt-6">
          <BaseButtonSecondary
            type="button"
            onClick={props.onClose}
            className="px-6 py-2"
          >
            Cancel
          </BaseButtonSecondary>
          <BaseButtonPrimary
            type="submit"
            disabled={isSubmitting}
            isBusy={isSubmitting}
            className="px-6 py-2"
          >
            Save Changes
          </BaseButtonPrimary>
        </div>
      </form>
    </BaseModal>
  );
}
